export type CsvColumn<T> = {
  header: string;
  value: (row: T) => string | number | null | undefined;
};

type CsvOptions = {
  /** Excel on Windows reads a BOM-less CSV as ANSI and mangles accents/dashes. */
  withBom?: boolean;
};

const neutralizeFormula = (s: string): string => (/^[=+\-@\t\r]/.test(s) && !Number.isFinite(Number(s)) ? `'${s}` : s);

// RFC 4180: quote cells with commas, quotes, or newlines (CR or LF);
// escape inner quotes by doubling. Anything else passes through.
const escapeCell = (v: string | number | null | undefined): string => {
  if (v == null) return '';
  if (typeof v === 'number') return String(v);
  const s = neutralizeFormula(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const rowsToCsv = <T>(rows: T[], columns: CsvColumn<T>[]): string => {
  const header = columns.map(c => escapeCell(c.header)).join(',');
  const lines = rows.map(row => columns.map(c => escapeCell(c.value(row))).join(','));
  return [header, ...lines].join('\n');
};

export const downloadRowsAsCsv = <T>(
  rows: T[],
  columns: CsvColumn<T>[],
  filename: string,
  options: CsvOptions = {},
): void => {
  const csv = rowsToCsv(rows, columns);
  const blob = new Blob([options.withBom ? `\uFEFF${csv}` : csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
